import { lazy, Suspense, useContext, useEffect } from 'react'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import AuthWrapper from './components/wrappers/AuthWrapper'
import ErrorBoundary from './components/common/ErrorBoundary'

import UserWrapper from './components/wrappers/UserWrapper'
import { AuthContext, useAuth } from './hooks/auth/useAuth'
import Loading from './pages/Loading'
import OrganizationPage from './pages/user/Organization/OrganizationPage'

const HomePage = lazy(() => import('./pages/user/HomePage/HomePage'))
const DocumentPage = lazy(() => import('./pages/user/Document/DocumentPage'))
const PayrollPage = lazy(() => import('./pages/user/Payroll/PayrollPage'))
const EvaluationsPage = lazy(() => import('./pages/user/Evaluations/EvaluationsPage'))
const PlanningPage = lazy(() => import('./pages/user/Planning/PlanningPage'))
const UserWorkforcePage = lazy(() => import('./pages/user/Workforce/UserWorkforce'))
const ContentPage = lazy(() => import('./pages/user/Content/ContentPage'))
const RecruitmentPage = lazy(() => import('./pages/user/Recruitment/RecruitmentPage'))
const ClassificationPage = lazy(() => import('./pages/user/Classification/ClassificationPage'))
const SettingsPage = lazy(() => import('./pages/user/Settings/SettingsPage'))

const AboutPeople = lazy(() => import('./pages/user/Workforce/AboutPeople'))
const Login = lazy(() => import('./pages/auth/LoginPage'))
const Register = lazy(() => import('./pages/auth/RegisterPage'))
const ForgotPass = lazy(() => import('./pages/auth/ForgotPage'))
const Verification = lazy(() => import('./pages/auth/VerificationPage'))
const ResetPass = lazy(() => import('./pages/auth/ResetPage'))
const ResetSuccess = lazy(() => import('./pages/auth/ResetSuccess'))
const InvitePeople = lazy(() => import('./pages/auth/InvitePage'))
const ProfilePage = lazy(() => import('./pages/user/Profile/ProfilePage'))
const AboutOrganization = lazy(() => import('./components/tables/AboutOrgTable'))
const AboutTeam = lazy(() => import('./pages/user/Workforce/AboutTeam'))
const AboutJob = lazy(() => import('./pages/user/Classification/AboutJob'))
const AboutSkills = lazy(() => import('./pages/user/Classification/AboutSkills'))
const AboutVacancy = lazy(() => import('./pages/user/Recruitment/AboutVacancy'))
const PathPage = lazy(() => import('./pages/user/Paths/PathPage'))
const UserPath = lazy(() => import('./pages/user/Paths/UserPath'))
const UserPathPage = lazy(() => import('./pages/user/Paths/UserPathPage'))
const AddPathPage = lazy(() => import('./pages/user/Paths/AddPaths'))
const AboutContent = lazy(() => import('./pages/user/Content/aboutContent'))
const QuestionnaireResponses = lazy(() => import('./pages/user/Content/QuestionnaireResponses'))
const ContentEditorPage = lazy(() => import('./pages/user/Content/ContentEditorPage'))
const LegacyContentAuthoringRedirect = lazy(() => import('./pages/user/Content/LegacyContentAuthoringRedirect'))
const ExplorePage = lazy(() => import('./pages/user/Explore/ExplorePage'))
const TodoPage = lazy(() => import('./pages/user/Todo/TodoPage'))
const RoleDetail = lazy(() => import('./pages/user/Settings/RoleDetail'))
const EvaluationQuestions = lazy(() => import('./pages/user/Evaluations/EvaluationQuestions'))
const DocumentationPage = lazy(() => import('./pages/user/Documentation/DocumentationPage'))
const MfaPage = lazy(() => import('./pages/auth/MfaPage'))
const MfaResetPage = lazy(() => import('./pages/auth/MfaResetPage'))
const EmailVerificationPage = lazy(() => import('./pages/auth/EmailVerificationPage'))
const SelectPlanPage = lazy(() => import('./pages/auth/SelectPlanPage'))

const AboutCandidate = lazy(() => import('./pages/user/Recruitment/AboutCandidate'))
const AboutActivity = lazy(() => import('./pages/user/TimeTracking/AboutActivity'))
const AIChatV2Page = lazy(() => import('./components/ai-chat-v2/AIChatV2Page'))
const HRChatPage = lazy(() => import('./components/hr-chat/HRChatPage'))
const FAQPage = lazy(() => import('./pages/user/FAQ/FAQPage'))
const FAQAdminPage = lazy(() => import('./pages/user/FAQ/FAQAdminPage'))
const AnalyticsPage = lazy(() => import('./pages/user/Analytics/AnalyticsPage'))
const OutflowPage = lazy(() => import('./pages/user/Outflow/OutflowPage'))
const ProjectDetailPage = lazy(() => import('./pages/user/TimeTracking/ProjectDetailPage'))



const PrivateUserRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated } = useContext(AuthContext)

  if (!isAuthenticated) return <Navigate to="/auth/login" />
  return (
    <UserWrapper>
      <Suspense fallback={null}>{children}</Suspense>
    </UserWrapper>
  )
}

const PublicAuthRoute = ({ children }: { children: React.ReactNode }) => {
  const auth = useContext(AuthContext)
  const currentPath = window.location.pathname
  const searchParams = new URLSearchParams(window.location.search)
  const mode = searchParams.get('mode')

  // Allow MFA, verification pages, and reset page with verifyEmail/revertSecondFactorAddition mode even if user is partially authenticated
  const isAllowedAuthPage =
    currentPath === '/auth/mfa' ||
    currentPath === '/auth/verification' ||
    currentPath === '/auth/verify-email' ||
    currentPath === '/auth/reset-mfa' ||
    currentPath === '/auth/register' || // TEMP: Allow registration page for testing
    currentPath === '/auth/select-plan' ||
    (currentPath === '/auth/resetpass' && (mode === 'verifyEmail' || mode === 'revertSecondFactorAddition'))

  // If user is authenticated and verified, check if MFA is required (same as login: only when not yet completed this session)
  if (auth.isAuthenticated && auth.isVerified && !isAllowedAuthPage) {
    const mustCompleteMFA = auth.requiresMFA || (auth.mfaRequiredForUser && !auth.mfaVerifiedThisSession)
    if (mustCompleteMFA) {
      return <Navigate to="/auth/mfa" replace />
    }
    return <Navigate to="/user/home" replace />
  }

  return (
    <Suspense fallback={<Loading />}>
      {currentPath === '/auth/select-plan' ? children : <AuthWrapper>{children}</AuthWrapper>}
    </Suspense>
  )
}

const App = () => {
  const auth = useAuth()
  const { i18n } = useTranslation()

  // Change language based on user preference
  useEffect(() => {
    if (auth.user?.language && i18n.language !== auth.user.language) {
      i18n.changeLanguage(auth.user.language)
    }
  }, [auth.user?.language, i18n])

  if (auth.loading.query) return <Loading />
  return (
    <AuthContext.Provider value={auth}>
      <BrowserRouter>
        <Routes>
          
          <Route
            path="/auth/*"
            element={
              <PublicAuthRoute>
                <Routes>
                  <Route path="/login" element={<Login />} />
                  <Route path="/register" element={<Register />} />
                  <Route path="/forgotpass" element={<ForgotPass />} />
                  <Route path="/verification" element={<Verification />} />
                  <Route path="/resetpass" element={<ResetPass />} />
                  <Route path="/resetsuccess" element={<ResetSuccess />} />
                  <Route path="/invite" element={<InvitePeople />} />
                  <Route path="/mfa" element={<MfaPage />} />
                  <Route path="/verify-email" element={<EmailVerificationPage />} />
                  <Route path="/reset-mfa" element={<MfaResetPage />} />
                  <Route path="/select-plan" element={<SelectPlanPage />} />
                </Routes>
              </PublicAuthRoute>
            }
          />
          <Route
            path="/user/*"
            element={
              <ErrorBoundary>
                <PrivateUserRoute>
                <Routes>
                  <Route path="/home" element={<HomePage />} />
                  <Route path="/home/:id" element={<HomePage />} />
                  <Route path="/classification/jobprofile" element={<ClassificationPage selectedTab="jobprofile" />} />
                  <Route path="/classification/skills" element={<ClassificationPage selectedTab="skills" />} />
                  <Route path="/classification/roles" element={<ClassificationPage selectedTab="roles" />} />
                  <Route path="/classification/accountability" element={<ClassificationPage selectedTab="accountability" />} />
                  <Route path="classification/jobprofile/:id" element={<AboutJob />} />
                  <Route path="classification/skills/:id" element={<AboutSkills />} />
                  <Route path="/workforce/people" element={<UserWorkforcePage selectedTab="people" />} />
                  <Route path="/workforce/people/:id" element={<AboutPeople />} />
                  <Route path="/workforce/teams/:id" element={<AboutTeam />} />
                  <Route path="/workforce/teams" element={<UserWorkforcePage selectedTab="teams" />} />
                  <Route path="/workforce/organization" element={<UserWorkforcePage selectedTab="organization" />} />
                  <Route path="/workforce/organization/:id" element={<AboutOrganization />} />
                  <Route path="/workforce/assets" element={<UserWorkforcePage selectedTab="assets" />} />
                  <Route path="/workforce/edithistory" element={<UserWorkforcePage selectedTab="edithistory" />} />
                  {/* Client-side role guard — backend enforces permissions server-side */}
                  <Route
                    path="/workforce/mutations"
                    element={
                      (() => {
                        const role = typeof window !== 'undefined' ? localStorage.getItem('role') : null
                        const allowed = ['super_admin', 'country_admin', 'org_admin', 'finance']
                        return allowed.includes(role || '') ? (
                          <UserWorkforcePage selectedTab="mutations" />
                        ) : (
                          <Navigate to="/user/workforce/people" />
                        )
                      })()
                    }
                  />
                  <Route path="/organization/organization" element={<OrganizationPage />} />  {/* MUST come before :id route */}
                  <Route path="/organization" element={<OrganizationPage />} />
                  <Route path="/organization/:id" element={<AboutTeam  />} />
                  <Route path="/organization/:type" element={<OrganizationPage />} />
                  <Route path="/profile" element={<ProfilePage />} />
                  <Route path="/document" element={<DocumentPage isUserPage={true} />} />
                  <Route path="/document/:type" element={<DocumentPage isUserPage={true} />} />
                  <Route path="/admin/document/:type" element={<DocumentPage />} />
                  <Route path="/content" element={<Navigate to="/user/explore/library" replace />} />
                  <Route path="/content/:id" element={<AboutContent />} />
                  <Route path="/content/new" element={<ContentEditorPage />} />
                  <Route path="/content/:id/edit" element={<ContentEditorPage />} />
                  <Route path="/content/:id/edit-items" element={<LegacyContentAuthoringRedirect />} />
                  <Route path="/content/:id/questionnaire" element={<LegacyContentAuthoringRedirect />} />
                  <Route path="/content/:id/questionnaire/responses/summary" element={<QuestionnaireResponses />} />
                  <Route path="/todo" element={<TodoPage />} />
                  <Route path="/questionnaire/create" element={<LegacyContentAuthoringRedirect />} />
                  <Route path="/questionnaire/addcontentitems" element={<LegacyContentAuthoringRedirect />} />  

                  <Route path="/explore/paths" element={<PathPage />} />
                  <Route path="/explore/paths/:id" element={<AddPathPage />} />

                  <Route path="/explore/userpath" element={<UserPath />} />
                  <Route path="/explore/userpath/:id" element={<UserPathPage />} />

                  <Route path="/explore/content" element={<ExplorePage />} />
                  <Route path="/explore/library" element={<ContentPage selectedTab="library" />} />
                  <Route path="/explore/statistics" element={<ContentPage selectedTab="statistics" />} />

                  <Route path="/payroll/people" element={<PayrollPage selectedTab="people" />} />
                  <Route path="/payroll/benefits" element={<PayrollPage selectedTab="benefits" />} />
                  {/* Client-side role guard — backend enforces permissions server-side */}
                  <Route
                    path="/payroll/benefit-mutations"
                    element={
                      (() => {
                        const role = typeof window !== 'undefined' ? localStorage.getItem('role') : null
                        return ['super_admin', 'org_admin'].includes(role || '') ? (
                          <PayrollPage selectedTab="benefit-mutations" />
                        ) : (
                          <Navigate to="/user/payroll/people" />
                        )
                      })()
                    }
                  />

                  <Route path="/evaluations" element={<EvaluationsPage/>} />
                  <Route path="/evaluations/questions" element={<EvaluationQuestions />} />

                  {/* Client-side role guard reads from localStorage (same source as useRole hook).
                     This is a UX convenience only — the backend enforces permissions server-side. */}
                  {(() => {
                    const role = typeof window !== 'undefined' ? localStorage.getItem('role') : null
                    const guard = (el: React.ReactNode) => ['super_admin', 'org_admin'].includes(role || '') ? el : <Navigate to="/user/home" replace />
                    return (
                      <>
                        <Route path="/settings" element={guard(<Navigate to="/user/settings/general" replace />)} />
                        <Route path="/settings/general" element={guard(<SettingsPage selectedTab="general" />)} />
                        <Route path="/settings/integration" element={guard(<SettingsPage selectedTab="integration" />)} />
                        <Route path="/settings/access" element={guard(<SettingsPage selectedTab="access" />)} />
                        <Route path="/settings/access/roles/:id" element={guard(<RoleDetail />)} />
                        <Route path="/settings/payments" element={guard(<SettingsPage selectedTab="payments" />)} />
                        <Route path="/settings/modules" element={guard(<SettingsPage selectedTab="modules" />)} />
                        <Route path="/settings/organizations" element={guard(<SettingsPage selectedTab="organizations" />)} />
                        <Route path="/settings/plans" element={guard(<SettingsPage selectedTab="plans" />)} />
                      </>
                    )
                  })()}

                  <Route path="/planning/attendance-calendar" element={<PlanningPage selectedTab="attendance-calendar" />} />
                  <Route path="/planning/past-requests" element={<PlanningPage selectedTab="past-requests" />} />
                  <Route path="/planning/attendance" element={<PlanningPage selectedTab="attendance" />} />
                  <Route path="/planning/leave-management" element={<PlanningPage selectedTab="leave-management" />} />
                  <Route path="/planning/workspace" element={<PlanningPage selectedTab="workspace" />} />
                  <Route path="/planning/timeline" element={<PlanningPage selectedTab="timeline" />} />
                  <Route path="/planning/desks" element={<PlanningPage selectedTab="desks" />} />
                  <Route path="/planning/rooms" element={<PlanningPage selectedTab="rooms" />} />
                  <Route path="/planning/offices" element={<PlanningPage selectedTab="offices" />} />
                  <Route path="/planning" element={<Navigate to="/user/planning/attendance-calendar" replace />} />
                  <Route path="/calendar" element={<Navigate to="/user/planning/attendance-calendar" replace />} />
                  <Route path="/calendar/admin" element={<Navigate to="/user/planning/attendance-calendar" replace />} />
                  {/* Old planning sub-routes now handled by unified PlanningPage */}

                  <Route path="/recruitment/candidates" element={<RecruitmentPage selectedTab="candidates" />} />
                  <Route path="/recruitment/candidates/:id" element={<AboutCandidate />} />
                  <Route path="/recruitment/pipeline" element={<RecruitmentPage selectedTab="pipeline" />} />
                  <Route path="/recruitment/interviews" element={<RecruitmentPage selectedTab="interviews" />} />
                  <Route path="/recruitment/vacancy" element={<RecruitmentPage selectedTab="vacancy" />} />
                  <Route path="/recruitment/vacancy/:id" element={<AboutVacancy />} />
                  <Route path="/recruitment/reports" element={<RecruitmentPage selectedTab="reports" />} />
                  <Route path="/recruitment/overview" element={<RecruitmentPage selectedTab="overview" />} />
                  <Route path="/recruitment/inflow" element={<RecruitmentPage selectedTab="inflow" />} />
                  <Route path="/recruitment" element={<Navigate to="/user/recruitment/candidates" replace />} />

                   <Route path="/documentation" element={<DocumentationPage />} />

                  <Route path="/chat" element={<AIChatV2Page />} />
                  <Route path="/chat-v2" element={<Navigate to="/user/chat" replace />} />
                  <Route path="/hr-chat" element={<HRChatPage />} />

                  <Route path="/faq" element={<FAQPage />} />
                  <Route path="/faq/manage" element={<FAQAdminPage />} />

                  <Route path="/analytics" element={<AnalyticsPage />} />

                  <Route path="/outflow/exit-management" element={<OutflowPage selectedTab="exit-management" />} />
                  <Route path="/outflow/exit-interviews" element={<OutflowPage selectedTab="exit-interviews" />} />
                  <Route path="/outflow/outstanding-assets" element={<OutflowPage selectedTab="outstanding-assets" />} />
                  <Route path="/outflow/turnover-reports" element={<OutflowPage selectedTab="turnover-reports" />} />
                  <Route path="/outflow" element={<Navigate to="/user/outflow/exit-management" replace />} />

                  <Route path="/planning/time-tracking" element={<PlanningPage selectedTab="time-tracking" />} />
                  <Route path="/planning/time-tracking/projects/:id" element={<ProjectDetailPage />} />
                  <Route path="/planning/time-tracking/activities/:id" element={<AboutActivity />} />

                </Routes>
              </PrivateUserRoute>
              </ErrorBoundary>
            }
          />
          <Route path="/" element={<Navigate to="/auth/login" />} />
        </Routes>
      </BrowserRouter>
    </AuthContext.Provider>
  )
}

export default App
